// Admin API client. Wraps fetch calls to /admin/api/* with the same
// credentials: 'include' pattern as the rest of the app — Supabase
// session cookies ride along. Server-side requireAdmin gates the
// routes; this client doesn't pre-check role.

import type { AppRole } from '@gutterinv/shared';

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role: AppRole;
  active: boolean;
  userId: string | null;
  createdAt: string;
  lastSignInAt: string | null;
  authCreatedAt: string | null;
}

// Analytics — 6-KPI shape (page views, unique visitors, authed users,
// logins, failed logins, bot hits). Backed by analytics_events
// (pageview ingest) joined with auth_events (login activity).
export interface AnalyticsSummary {
  pageViews: number;
  uniqueVisitors: number;
  authedUsers: number;
  totalLogins: number;
  failedLogins: number;
  botHits: number;
  windowStart: string | null;
  days: number;
}

export interface AnalyticsTimelinePoint {
  day: string;
  page_views: number;
  logins: number;
  bot_hits: number;
}

export interface AnalyticsTimeline {
  days: number;
  timeline: AnalyticsTimelinePoint[];
}

export interface AnalyticsVisitor {
  ip_hash: string;
  events: number;
  page_views: number;
  country: string | null;
  user_agent: string | null;
  is_bot: boolean;
  user_email: string | null;
  user_id: string | null;
  first_seen: string;
  last_seen: string;
}

export interface AnalyticsVisitorsResponse {
  visitors: AnalyticsVisitor[];
}

async function adminGet<T>(path: string): Promise<T> {
  const res = await fetch(`/admin/api${path}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`GET /admin/api${path} failed: ${res.status} ${body}`);
  }
  return (await res.json()) as T;
}

async function adminJson<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/admin/api${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `${method} /admin/api${path} failed: ${res.status} ${text}`,
    );
  }
  return (await res.json()) as T;
}

export function getAdminUsers(): Promise<{ users: AdminUser[] }> {
  return adminGet('/users');
}

export function inviteAdminUser(input: {
  email: string;
  role: AppRole;
}): Promise<{ ok: boolean; user: AdminUser; warning?: string }> {
  return adminJson('POST', '/users', input);
}

export function resendAdminInvite(userId: string): Promise<{ ok: boolean }> {
  return adminJson('POST', `/users/${userId}/resend-invite`);
}

export function patchAdminUser(
  userId: string,
  input: { role?: AppRole; active?: boolean; displayName?: string },
): Promise<{ ok: boolean; user: AdminUser }> {
  return adminJson('PATCH', `/users/${userId}`, input);
}

export function deleteAdminUser(userId: string): Promise<{ ok: boolean }> {
  return adminJson('DELETE', `/users/${userId}`);
}

export function forceSignOutUser(userId: string): Promise<{ ok: boolean }> {
  return adminJson('POST', `/users/${userId}/force-signout`);
}

export interface UserRecentLogin {
  id: string;
  eventType: 'login_success' | 'login_fail';
  ipHash: string | null;
  userAgent: string | null;
  country: string | null;
  createdAt: string;
}

export function getUserRecentLogins(
  userId: string,
  limit = 10,
): Promise<{ events: UserRecentLogin[] }> {
  return adminGet(`/users/${userId}/recent-logins?limit=${limit}`);
}

export function getAnalyticsSummary(days = 30): Promise<AnalyticsSummary> {
  return adminGet(`/analytics/summary?days=${days}`);
}

export function getAnalyticsTimeline(days = 30): Promise<AnalyticsTimeline> {
  return adminGet(`/analytics/timeline?days=${days}`);
}

export function getAnalyticsVisitors(
  days = 30,
  limit = 25,
): Promise<AnalyticsVisitorsResponse> {
  return adminGet(`/analytics/visitors?days=${days}&limit=${limit}`);
}

export interface FailedLoginEvent {
  id: string;
  email: string | null;
  ipHash: string | null;
  userAgent: string | null;
  country: string | null;
  reason: string | null;
  createdAt: string;
}

export function getAnalyticsFailedLogins(
  days = 30,
  limit = 25,
): Promise<{ events: FailedLoginEvent[] }> {
  return adminGet(`/analytics/failed-logins?days=${days}&limit=${limit}`);
}

export interface AnalyticsEvent {
  source: 'pageview' | 'auth';
  createdAt: string;
  eventType: string; // 'pageview' | 'login_send' | 'login_success' | 'login_fail'
  userEmail: string | null;
  ipHash: string | null;
  userAgent: string | null;
  country: string | null;
  isBot: boolean;
  path: string | null;
  detail: Record<string, unknown> | null;
}

export interface AnalyticsEventsResponse {
  days: number;
  events: AnalyticsEvent[];
}

// --- Document storage (R2) reconciliation ---
export interface R2OrphanReport {
  /** Files in storage with no matching document record (safe to clean up). */
  orphanObjects: string[];
  /** Document records whose file is missing from storage (needs attention). */
  missingObjects: string[];
  /** Document records in the database. */
  dbCount: number;
  /** Files in storage. */
  r2Count: number;
}

export function getR2Orphans(): Promise<R2OrphanReport> {
  return adminGet('/r2-orphans');
}

export function purgeR2Orphans(): Promise<{ purged: number; missingObjects: string[] }> {
  return adminJson('POST', '/r2-orphans/purge');
}

export function getAnalyticsEventLog(
  days = 30,
  opts: { limit?: number; eventType?: string | null; userEmail?: string | null } = {},
): Promise<AnalyticsEventsResponse> {
  const params = new URLSearchParams({
    days: String(days),
    limit: String(opts.limit ?? 100),
  });
  if (opts.eventType && opts.eventType !== 'all') {
    params.set('event_type', opts.eventType);
  }
  if (opts.userEmail && opts.userEmail !== 'all') {
    params.set('user_email', opts.userEmail);
  }
  // URL is /event-log (not /events) to avoid EasyPrivacy/uBlock rules
  // that block ||/events?$xhr. See worker route comment + memory file
  // feedback_adblocker_blocks_analytics_paths.md.
  return adminGet(`/analytics/event-log?${params.toString()}`);
}
